/**
 * The single funnel for primary-CTA conversion intent.
 *
 * Why this exists: CTA clicks were previously measured two incompatible ways —
 * a homepage-only delegated listener firing `home_cta_click`, and UTM
 * parameters appended to INTERNAL hrefs (AnswerCTA). The latter is actively
 * destructive: a utm_* param on a same-site navigation makes GA4 start a new
 * session and overwrite the original acquisition source, so an organic visitor
 * who clicks a CTA is re-attributed to the CTA itself. UTMs belong on inbound
 * links only (email, social, share/badge embeds) where `first-touch-utm.ts`
 * already captures them.
 *
 * Every primary CTA now reports through here instead:
 * - GA4 + PostHog via `trackEvent` (consent-gated, non-blocking).
 * - Microsoft Clarity via a `cta_id` custom tag, so the session recordings and
 *   heatmaps for a given CTA can be filtered directly.
 *
 * Two entry points, one `cta_id` vocabulary: link CTAs are picked up
 * automatically by the delegated `CtaTracker` listener (any anchor inside a
 * `[data-cta-id]` carrier) and report `cta_click`; form CTAs, which have no
 * anchor to delegate from, call `trackCtaConversion` on successful submit and
 * keep their own established GA4 event name.
 *
 * Params are non-PII by construction: a fixed `cta_id` vocabulary plus a
 * sanitized destination. The query string is always stripped — audited
 * domains, share ids and emails live there. Page context is deliberately NOT
 * passed: GA4 attaches `page_location` and PostHog attaches `$current_url` to
 * every event already, and re-sending the raw pathname would smuggle
 * identifier-bearing routes (`/r/<share-id>`) into the params.
 */

import { trackEvent } from '@/lib/track-event'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'

/** Clarity caps both tag key and value at 255 characters. */
const MAX_DESTINATION_LENGTH = 200

/**
 * Reduce an href to a stable, non-identifying label.
 *
 * - `mailto:` keeps the address but drops `?subject=`/`?body=` payloads.
 * - Same-origin links become `pathname + hash` (the hash is kept: it carries
 *   real intent, e.g. `/platform#pricing`).
 * - Cross-origin links keep the origin so outbound destinations stay legible.
 * - The query string is dropped in every case.
 */
export function sanitizeDestination(href: string | null | undefined): string {
  const raw = href?.trim()
  if (!raw) return 'unknown'

  if (raw.toLowerCase().startsWith('mailto:')) {
    return raw.split('?')[0].slice(0, MAX_DESTINATION_LENGTH)
  }

  try {
    const base =
      typeof window === 'undefined'
        ? 'https://www.pixelmojo.io'
        : window.location.origin
    const url = new URL(raw, base)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return url.protocol.replace(':', '').slice(0, MAX_DESTINATION_LENGTH)
    }
    const path = `${url.pathname}${url.hash}`
    const sameOrigin =
      typeof window !== 'undefined' && url.origin === window.location.origin
    return (sameOrigin ? path : `${url.origin}${path}`).slice(
      0,
      MAX_DESTINATION_LENGTH
    )
  } catch {
    // Relative fragment or malformed href — keep the literal, minus any query.
    return raw.split('?')[0].slice(0, MAX_DESTINATION_LENGTH)
  }
}

/**
 * Tag the Clarity session with the CTA that was clicked.
 *
 * Guarded on an actual function: an element with `id="clarity"` (a generated
 * heading anchor, for instance) shadows `window.clarity` with an HTMLElement —
 * the same named-window-property collision the Clarity loader in layout.tsx
 * defends against. Also consent-gated independently of the loader, since the
 * loader only creates the queue stub after consent.
 */
function setClarityCtaTag(ctaId: string): void {
  try {
    if (!isAnalyticsConsentGranted()) return
    if (typeof window.clarity !== 'function') return
    window.clarity('set', 'cta_id', ctaId)
  } catch {
    // Analytics must never break the product.
  }
}

/**
 * Report a primary-CTA click to GA4, PostHog, and Clarity.
 *
 * Safe to call from any client context; no-ops server-side, without consent,
 * or when a given destination is unavailable.
 */
export function trackCtaClick(ctaId: string, href?: string | null): void {
  if (typeof window === 'undefined') return
  const id = ctaId?.trim()
  if (!id) return

  trackEvent('cta_click', {
    cta_id: id,
    destination: sanitizeDestination(href),
  })
  setClarityCtaTag(id)
}

/**
 * Report a CTA that converts by form submit rather than navigation.
 *
 * Deliberately NOT `cta_click`: an email-capture submit is a conversion, not a
 * click, and folding it into the click series would make the CTA-level
 * click-through rate meaningless. The caller keeps its own established GA4
 * event name (renaming those would break existing history) — this only routes
 * it through the consent-gated helper so PostHog receives it too, and applies
 * the same Clarity session tag the link CTAs use, so recordings filter by
 * `cta_id` no matter which kind of CTA converted.
 */
export function trackCtaConversion(
  ctaId: string,
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (typeof window === 'undefined') return
  const id = ctaId?.trim()
  if (!id || !eventName) return

  trackEvent(eventName, { ...params, cta_id: id })
  setClarityCtaTag(id)
}
